import { tagUploadRefusalTrigger } from "./uploadFacadeErrors";
import type {
	ChunkedUploadDriverPorts,
	DriverFile,
	MultipartSessionShape,
	MultipartUrlRefreshBatch,
	MultipartUrlRefreshResult,
	MultipartUrlRefreshTargetMigration,
	UploadLease,
	UploadTelemetry
} from "./ports";
import { UploadOwnershipLostError } from "./uploadChunk";
import { hasValidMultipartUrls, isValidPresignedUrl } from "./multipartValidation";
import { runGuardedEffect } from "./ownershipGate";
import {
	createUploadsMultipartUploadTarget,
	persistedUploadTarget,
	persistedUploadTargetChunkHeaders,
	type UploadsMultipartUploadTarget
} from "./targetInitiation";
import { uploadServerRefusalLogDetails } from "./uploadTelemetry";

function mergeRefreshedEnvelopeHeaders(active: Readonly<Record<string, string>>[], updates: readonly MultipartUrlRefreshResult[]): void {
	for (const update of updates) if (update.envelope) active[update.partNumber - 1] = update.envelope.headers;
}

/**
 * Refresh responses are allowed to replace a signed URL, but never the opaque
 * target that URL is for. The session row is the current target fence; validate
 * against it before either persisting the URL or exposing refreshed headers.
 */
function assertDirectRefreshTargetFence<Session extends MultipartSessionShape>(
	session: Session,
	updates: readonly MultipartUrlRefreshResult[],
	targetMigration?: MultipartUrlRefreshTargetMigration
): void {
	if (session.endpointFamily !== "uploads") {
		if (updates.some((update) => update.envelope !== undefined)) {
			throw new MultipartUrlRefreshError("Legacy multipart URL refresh returned an opaque uploads target envelope");
		}
		return;
	}
	if (targetMigration) return;

	// The rebuild goes through persistedUploadTarget so the row's minted `transport` survives the
	// re-mint below: `{ ...currentTarget, envelopes }` can only carry a transport that currentTarget
	// already has, and a dropped one reads back as "multipart" on a chunked-object resume.
	let currentTarget: UploadsMultipartUploadTarget | undefined;
	try {
		currentTarget = persistedUploadTarget(session);
	} catch (error) {
		throw new MultipartUrlRefreshError("Persisted uploads target envelopes are invalid", error);
	}
	// `persistedUploadTarget` answers undefined only for a non-`uploads` family row. The guard above
	// already established `endpointFamily === "uploads"`, so a silent `!` here was safe only by
	// coincidence and would crash further down with a bare "cannot read envelopes". Name the row
	// instead, the same way the two contact-admin refresh sites do
	// (`adminSessionStore.ts` / `adminChunkedUploadDriver.ts`).
	if (!currentTarget) {
		throw new MultipartUrlRefreshError(
			`Cannot refresh uploads session ${session.registryId ?? "(no registryId)"} attempt ${session.attemptId ?? "(no attemptId)"}: persisted row is not an uploads-family target`
		);
	}

	const refreshedEnvelopes = [...currentTarget.envelopes];
	for (const update of updates) {
		const refreshed = update.envelope;
		const current = currentTarget.envelopes[update.partNumber - 1];
		if (!refreshed || !current) {
			throw new MultipartUrlRefreshError("Multipart URL refresh target fence does not match the persisted current target");
		}
		refreshedEnvelopes[update.partNumber - 1] = refreshed;
	}

	let refreshedTarget: UploadsMultipartUploadTarget;
	try {
		refreshedTarget = createUploadsMultipartUploadTarget({
			...currentTarget,
			envelopes: refreshedEnvelopes
		});
	} catch (error) {
		throw new MultipartUrlRefreshError("Multipart URL refresh returned an invalid target envelope", error);
	}
	for (const update of updates) {
		const refreshed = refreshedTarget.envelopes[update.partNumber - 1];
		const current = currentTarget.envelopes[update.partNumber - 1];
		if (
			!refreshed ||
			!current ||
			refreshed.ordinal !== current.ordinal ||
			refreshed.targetId !== current.targetId ||
			refreshed.attemptId !== current.attemptId ||
			refreshed.generation !== current.generation ||
			refreshed.url !== update.url
		) {
			throw new MultipartUrlRefreshError("Multipart URL refresh target fence does not match the persisted current target");
		}
	}
}

type NormalizedRefreshBatch = {
	updates: MultipartUrlRefreshResult[];
	targetMigration?: MultipartUrlRefreshTargetMigration;
};

function normalizeRefreshBatch<Session extends MultipartSessionShape>(
	session: Session,
	response: MultipartUrlRefreshBatch
): NormalizedRefreshBatch {
	const updates = response;
	if (session.endpointFamily !== "uploads") {
		if (response.replacementTarget) {
			throw new MultipartUrlRefreshError("Legacy multipart URL refresh cannot migrate an uploads target");
		}
		return { updates };
	}

	const replacementTarget = response.replacementTarget;
	// Compatibility for existing MultipartApiPort implementations and focused driver tests that
	// return the historical array shape. Production app adapters always carry the authenticated
	// top-level response as replacementTarget. An envelope-only response cannot prove which registry
	// issued it, so it may describe routine refreshes only; migrations require the authenticated
	// replacementTarget supplied by the adapter.
	if (!replacementTarget && updates.length === session.totalChunks) {
		const envelopes = updates.map(({ envelope }) => envelope);
		const first = envelopes[0];
		if (
			first &&
			first.attemptId !== session.attemptId &&
			first.generation > (session.generation ?? 0) &&
			envelopes.every(
				(envelope, index) =>
					envelope?.attemptId === first.attemptId && envelope.generation === first.generation && envelope.ordinal === index + 1
			)
		) {
			throw new MultipartUrlRefreshError(
				"Multipart URL refresh migration requires an explicit replacementTarget with authenticated registry identity"
			);
		}
	}

	if (!replacementTarget) return { updates };
	if (session.ephemeral) throw new MultipartUrlRefreshError("An ephemeral multipart refresh cannot migrate its upload attempt");
	if (
		replacementTarget.registryId !== session.registryId ||
		replacementTarget.attemptId === session.attemptId ||
		!(replacementTarget.generation > (session.generation ?? 0)) ||
		(session.takeGeneration !== undefined && replacementTarget.takeGeneration !== session.takeGeneration)
	) {
		throw new MultipartUrlRefreshError("Multipart URL refresh returned an invalid session target migration");
	}
	let normalizedTarget: ReturnType<typeof createUploadsMultipartUploadTarget>;
	try {
		normalizedTarget = createUploadsMultipartUploadTarget(replacementTarget);
	} catch (error) {
		throw new MultipartUrlRefreshError("Multipart URL refresh returned an invalid replacement target", error);
	}
	if (normalizedTarget.envelopes.length !== session.totalChunks) {
		throw new MultipartUrlRefreshError("Multipart URL refresh migration did not replace every target envelope");
	}
	const normalizedUpdates = normalizedTarget.envelopes.map((envelope) => ({
		partNumber: envelope.ordinal,
		url: envelope.url,
		envelope
	}));
	return {
		updates: normalizedUpdates,
		targetMigration: {
			previousAttemptId: session.attemptId ?? "",
			previousGeneration: session.generation ?? 0,
			replacementTarget: normalizedTarget
		}
	};
}

export const PRESIGNED_URL_TTL_MS = 14_400 * 1_000;
export const PRESIGNED_URL_SAFETY_MARGIN_MS = 10 * 60 * 1_000;

export function isPresignedUrlAgeValid(urlCreatedAt: number, now: number): boolean {
	if (!Number.isFinite(urlCreatedAt) || !Number.isFinite(now) || urlCreatedAt > now) return false;
	return now - urlCreatedAt < PRESIGNED_URL_TTL_MS - PRESIGNED_URL_SAFETY_MARGIN_MS;
}

export class MultipartUrlRefreshError extends Error {
	readonly originalError?: unknown;
	declare readonly isAmbiguousTransportWrap: boolean;
	#isAmbiguousTransportWrap: boolean;

	constructor(message: string, originalError?: unknown, isAmbiguousTransportWrap = false) {
		super(message);
		this.name = "MultipartUrlRefreshError";
		this.originalError = originalError;
		this.#isAmbiguousTransportWrap = isAmbiguousTransportWrap;
		Object.defineProperty(this, "isAmbiguousTransportWrap", {
			configurable: false,
			enumerable: true,
			get: () => this.#isAmbiguousTransportWrap
		});
	}
}

/**
 * Validates the authenticated attempt identity carried by a ListParts response. A response without
 * identity fields is the legacy/non-migrating shape; once any identity field is present, the whole
 * migration announcement must be internally consistent before the caller enters URL adoption.
 */
export function announcesMultipartAttemptMigration(session: MultipartSessionShape, response: unknown): boolean {
	if (session.endpointFamily !== "uploads" || !response || typeof response !== "object") return false;
	if (Array.isArray(response)) {
		if ("attempt" in response || "attemptId" in response || "generation" in response) {
			throw new MultipartUrlRefreshError("Uploads ListParts returned an invalid attempt migration announcement");
		}
		return false;
	}
	const candidate = response as {
		id?: unknown;
		attemptId?: unknown;
		generation?: unknown;
		attempt?: {
			id?: unknown;
			generation?: unknown;
			takeGeneration?: unknown;
			transport?: unknown;
		} | null;
	};
	const carriesMigrationAnnouncement =
		"id" in candidate || "attempt" in candidate || "attemptId" in candidate || "generation" in candidate;
	if (!carriesMigrationAnnouncement) return false;
	if (
		!Object.prototype.hasOwnProperty.call(candidate, "attempt") ||
		!Object.prototype.hasOwnProperty.call(candidate, "attemptId") ||
		!Object.prototype.hasOwnProperty.call(candidate, "generation")
	) {
		throw new MultipartUrlRefreshError("Uploads ListParts returned an invalid attempt migration announcement");
	}
	if (
		candidate.id !== session.registryId ||
		candidate.attempt?.id !== candidate.attemptId ||
		candidate.attempt?.generation !== candidate.generation ||
		candidate.attempt?.transport !== "multipart" ||
		typeof candidate.attempt.takeGeneration !== "string" ||
		candidate.attempt.takeGeneration.length === 0 ||
		(session.takeGeneration !== undefined && candidate.attempt.takeGeneration !== session.takeGeneration)
	) {
		throw new MultipartUrlRefreshError("Uploads ListParts returned an invalid attempt migration announcement");
	}
	const migrated = candidate.attemptId !== session.attemptId || candidate.generation !== session.generation;
	if (!migrated) return false;
	if (
		typeof candidate.attemptId !== "string" ||
		candidate.attemptId.length === 0 ||
		candidate.attemptId === session.attemptId ||
		!Number.isSafeInteger(candidate.generation) ||
		(candidate.generation as number) <= (session.generation ?? 0)
	) {
		throw new MultipartUrlRefreshError("Uploads ListParts returned an invalid attempt migration announcement");
	}
	return true;
}

function logUrlWarning(telemetry: UploadTelemetry, event: string, context: Record<string, unknown>, error?: unknown): void {
	try {
		telemetry.logWarn(event, context, error);
	} catch {
		// URL fallback/refresh behavior must not depend on the observability adapter.
	}
}

function safeRefreshErrorMessage(error: unknown): string {
	try {
		if (error instanceof Error && typeof error.message === "string") return error.message;
	} catch {
		// Fall through to string coercion, then to a stable generic message.
	}
	try {
		return String(error);
	} catch {
		return "Multipart URL refresh failed";
	}
}

export function isMultipartUrlRefreshError(error: unknown): error is MultipartUrlRefreshError {
	try {
		return error instanceof MultipartUrlRefreshError;
	} catch {
		return false;
	}
}

export function isConfirmedMigrationIntegrityError(error: unknown): error is MultipartUrlRefreshError {
	try {
		return isMultipartUrlRefreshError(error) && !error.isAmbiguousTransportWrap;
	} catch {
		return false;
	}
}

async function runUrlRefreshStage<T>(
	operation: string,
	session: MultipartSessionShape,
	telemetry: UploadTelemetry,
	work: () => Promise<T>
): Promise<T> {
	try {
		return await work();
	} catch (error) {
		const isRefreshError = isMultipartUrlRefreshError(error);
		const diagnosticError = isRefreshError && error.originalError !== undefined ? error.originalError : error;
		tagUploadRefusalTrigger(diagnosticError, "urls");
		logUrlWarning(
			telemetry,
			"recording.fileUpload.urlRefreshFailed",
			{
				sessionId: session.id,
				uploadId: session.uploadId,
				...(session.registryId !== undefined ? { registryId: session.registryId } : {}),
				operation,
				// The fence this refresh presented, so a stale attempt/generation (404) or a refusal
				// (409) is attributable without reconstructing it from the browser's stored row.
				...(session.attemptId !== undefined ? { attemptId: session.attemptId } : {}),
				...(session.generation !== undefined ? { generation: session.generation } : {}),
				...uploadServerRefusalLogDetails(diagnosticError)
			},
			diagnosticError
		);
		if (isRefreshError) throw error;
		throw new MultipartUrlRefreshError(safeRefreshErrorMessage(error), error, true);
	}
}

export function parsePersistedChunkUrls(chunkUrlsJson: string, telemetry?: UploadTelemetry, session?: MultipartSessionShape): string[] {
	try {
		const parsed = JSON.parse(chunkUrlsJson);
		return Array.isArray(parsed) ? parsed : [];
	} catch (error) {
		if (telemetry && session) {
			logUrlWarning(
				telemetry,
				"recording.fileUpload.persistedUrlsParseFailed",
				{
					sessionId: session.id,
					uploadId: session.uploadId,
					operation: "parse-persisted-urls"
				},
				error
			);
		}
		return [];
	}
}

export function assertExactNonEmptyUrls(urls: unknown, expectedCount: number, operation: string): asserts urls is string[] {
	const actualCount = Array.isArray(urls) ? urls.length : 0;
	if (!hasValidMultipartUrls(urls, expectedCount)) {
		throw new MultipartUrlRefreshError(
			`${operation} returned ${actualCount}/${expectedCount} URLs (expected ${expectedCount} URL${expectedCount === 1 ? "" : "s"})`
		);
	}
}

export function assertExactPresignedUrls(urls: unknown, expectedCount: number, operation: string): asserts urls is string[] {
	assertExactNonEmptyUrls(urls, expectedCount, operation);
	if (urls.some((url) => !isValidPresignedUrl(url))) {
		throw new MultipartUrlRefreshError(`${operation} returned an invalid presigned URL`);
	}
}

function assertExactRefreshResults(
	results: unknown,
	expectedPartNumbers: readonly number[],
	operation: string
): asserts results is MultipartUrlRefreshResult[] {
	const actualCount = Array.isArray(results) ? results.length : 0;
	const expectedPartNumberSet = new Set(expectedPartNumbers);
	const actualPartNumbers = Array.isArray(results)
		? results.map((result) => (result && typeof result === "object" ? (result as { partNumber?: unknown }).partNumber : undefined))
		: [];
	const seenPartNumbers = new Set<number>();
	const hasExactUniquePartNumbers =
		actualCount === expectedPartNumbers.length &&
		actualPartNumbers.every(
			(partNumber) =>
				typeof partNumber === "number" &&
				expectedPartNumberSet.has(partNumber) &&
				!seenPartNumbers.has(partNumber) &&
				Boolean(seenPartNumbers.add(partNumber))
		);
	if (!hasExactUniquePartNumbers) {
		throw new MultipartUrlRefreshError(`${operation} returned an invalid part-number set (expected ${expectedPartNumbers.join(", ")})`);
	}
	const urls = (results as MultipartUrlRefreshResult[]).map(({ url }) => url);
	assertExactPresignedUrls(urls, expectedPartNumbers.length, operation);
}

function assertValidChunkIndex(chunkIndex: number, totalChunks: number): void {
	if (!Number.isInteger(chunkIndex) || chunkIndex < 0 || chunkIndex >= totalChunks) {
		throw new MultipartUrlRefreshError(
			`Multipart URL refresh chunk index ${chunkIndex} is outside the valid range [0, ${totalChunks})`
		);
	}
}

function isSafePersistedUploadUrl(url: string): boolean {
	return !url.startsWith("//") && (isValidPresignedUrl(url) || !/^[a-z][a-z\d+.-]*:/i.test(url));
}

export async function preparePersistedUploadUrls<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken>(input: {
	session: Session;
	pendingChunkIndexes: readonly number[];
	fallbackUrls: readonly string[];
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>;
	ownsCurrentSession?: () => Promise<boolean>;
	/** When present, URL persistence is committed under the app's writer epoch fence. */
	lease?: UploadLease<Session>;
	/** Forces the normal refresh/adoption path when ListParts reveals a migration after every chunk was uploaded. */
	forceRefresh?: boolean;
}): Promise<{
	chunkUrls: string[];
	chunkHeaders: Readonly<Record<string, string>>[];
	refreshPresignedUrl(
		chunkIndex: number,
		expiredUrl?: string,
		pendingChunkIndexes?: readonly number[],
		reason?: "expired" | "network"
	): Promise<string>;
}> {
	const { session, ports } = input;
	const persistedUrls = parsePersistedChunkUrls(session.chunkUrlsJson, ports.telemetry, session);
	const persistedUrlsAreValid =
		hasValidMultipartUrls(persistedUrls, session.totalChunks) && persistedUrls.every(isSafePersistedUploadUrl);
	const activeChunkUrls = (persistedUrlsAreValid ? persistedUrls : input.fallbackUrls).slice();
	const activeChunkHeaders = [...(persistedUploadTargetChunkHeaders(session) ?? [])];

	const mergeValidatedUrls = async (refresh: NormalizedRefreshBatch, refreshesAllPendingUrls = false): Promise<string[]> => {
		const { updates, targetMigration } = refresh;
		if (input.ownsCurrentSession && !(await input.ownsCurrentSession())) {
			throw new UploadOwnershipLostError();
		}
		const persistenceSession = targetMigration
			? ({
					...session,
					attemptId: targetMigration.replacementTarget.attemptId,
					generation: targetMigration.replacementTarget.generation
				} as Session)
			: session;
		const persistMergedUrls = () =>
			runUrlRefreshStage("persist-refreshed-urls", session, ports.telemetry, () =>
				targetMigration
					? ports.sessionStore.mergeAndPersistUrls(persistenceSession, updates, activeChunkUrls, true, targetMigration)
					: ports.sessionStore.mergeAndPersistUrls(persistenceSession, updates, activeChunkUrls, refreshesAllPendingUrls)
			);
		let merged: string[] | undefined;
		if (input.lease) {
			const mergeOutcome = await runGuardedEffect(input.lease, session, async () => {
				merged = await persistMergedUrls();
			});
			if (mergeOutcome === "skipped-not-owned") throw new UploadOwnershipLostError();
		} else {
			merged = await persistMergedUrls();
		}
		if (!merged) throw new UploadOwnershipLostError();
		if (input.ownsCurrentSession && !(await input.ownsCurrentSession())) {
			throw new UploadOwnershipLostError();
		}
		await runUrlRefreshStage("persist-refreshed-urls", session, ports.telemetry, async () => {
			assertExactNonEmptyUrls(merged, session.totalChunks, "Persisted multipart URL merge");
			if (merged.some((url) => !isSafePersistedUploadUrl(url))) {
				throw new MultipartUrlRefreshError("Persisted multipart URL merge returned an unsafe URL");
			}
		});
		activeChunkUrls.splice(0, activeChunkUrls.length, ...merged);
		if (targetMigration) {
			activeChunkHeaders.splice(
				0,
				activeChunkHeaders.length,
				...targetMigration.replacementTarget.envelopes.map(({ headers }) => headers)
			);
			Object.assign(session, {
				attemptId: targetMigration.replacementTarget.attemptId,
				generation: targetMigration.replacementTarget.generation,
				takeGeneration: targetMigration.replacementTarget.takeGeneration,
				targetEnvelopesJson: JSON.stringify(targetMigration.replacementTarget.envelopes),
				chunkUrlsJson: JSON.stringify(merged)
			});
		} else {
			mergeRefreshedEnvelopeHeaders(activeChunkHeaders, updates);
		}
		return activeChunkUrls;
	};

	if (
		input.pendingChunkIndexes.length > 0 &&
		(input.forceRefresh || !persistedUrlsAreValid || !isPresignedUrlAgeValid(session.urlCreatedAt, ports.now()))
	) {
		const partNumbers = persistedUrlsAreValid
			? input.pendingChunkIndexes.map((chunkIndex) => chunkIndex + 1)
			: Array.from({ length: session.totalChunks }, (_value, chunkIndex) => chunkIndex + 1);
		if (input.ownsCurrentSession && !(await input.ownsCurrentSession())) {
			throw new UploadOwnershipLostError();
		}
		const refreshedUrls = await runUrlRefreshStage("refresh-persisted-urls", session, ports.telemetry, async () => {
			const results = normalizeRefreshBatch(session, await ports.multipartApi.refreshUrls(session, partNumbers));
			assertExactRefreshResults(
				results.updates,
				results.targetMigration ? Array.from({ length: session.totalChunks }, (_value, index) => index + 1) : partNumbers,
				"Multipart URL refresh"
			);
			if (!results.targetMigration) assertDirectRefreshTargetFence(session, results.updates);
			return results;
		});
		await mergeValidatedUrls(refreshedUrls, true);
	}

	let activeBatchRefresh: Promise<void> | undefined;
	let refreshQueue = Promise.resolve();
	const enqueueRefresh = (work: () => Promise<void>): Promise<void> => {
		const queued = refreshQueue.then(work);
		refreshQueue = queued.then(
			() => undefined,
			() => undefined
		);
		return queued;
	};
	return {
		chunkUrls: activeChunkUrls,
		chunkHeaders: activeChunkHeaders,
		async refreshPresignedUrl(chunkIndex, expiredUrl, pendingChunkIndexes, reason) {
			assertValidChunkIndex(chunkIndex, session.totalChunks);
			// A network recovery must always request the regional lane. A concurrent expiry refresh may
			// have replaced this URL while the failed PUT was in flight, but that accelerated replacement
			// is not evidence that regional recovery happened.
			if (reason !== "network" && expiredUrl !== undefined && activeChunkUrls[chunkIndex] !== expiredUrl) {
				return activeChunkUrls[chunkIndex];
			}
			const refreshBatch = async () => {
				const refreshChunkIndexes = pendingChunkIndexes ?? [chunkIndex];
				const partNumbers = refreshChunkIndexes.map((pendingChunkIndex) => {
					assertValidChunkIndex(pendingChunkIndex, session.totalChunks);
					return pendingChunkIndex + 1;
				});
				const refreshOperation = partNumbers.length === 1 ? "refresh-persisted-part" : "refresh-persisted-parts";
				const refreshedUrls = await runUrlRefreshStage(refreshOperation, session, ports.telemetry, async () => {
					const results = normalizeRefreshBatch(
						session,
						reason === "network"
							? await ports.multipartApi.refreshUrls(session, partNumbers, true)
							: await ports.multipartApi.refreshUrls(session, partNumbers)
					);
					assertExactRefreshResults(
						results.updates,
						results.targetMigration ? Array.from({ length: session.totalChunks }, (_value, index) => index + 1) : partNumbers,
						"Multipart URL refresh"
					);
					if (!results.targetMigration) assertDirectRefreshTargetFence(session, results.updates);
					return results;
				});
				// Network recovery refreshes only the failed part. Do not advance the session-wide
				// URL age or imply sibling URLs were refreshed in that case.
				await mergeValidatedUrls(refreshedUrls, reason !== "network" && pendingChunkIndexes !== undefined);
			};
			if (reason === "network") {
				await enqueueRefresh(refreshBatch);
				return activeChunkUrls[chunkIndex];
			}
			activeBatchRefresh ??= enqueueRefresh(refreshBatch).finally(() => {
				activeBatchRefresh = undefined;
			});
			await activeBatchRefresh;
			return activeChunkUrls[chunkIndex];
		}
	};
}

export async function prepareDirectUploadUrls<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken>(input: {
	session: Session;
	chunkUrls: readonly string[];
	chunkHeaders?: readonly Readonly<Record<string, string>>[];
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>;
	ownsCurrentSession?: () => Promise<boolean>;
	/** When present, URL persistence is committed under the app's writer epoch fence. */
	lease?: UploadLease<Session>;
}): Promise<{
	chunkUrls: string[];
	chunkHeaders: Readonly<Record<string, string>>[];
	refreshPresignedUrl(
		chunkIndex: number,
		expiredUrl?: string,
		pendingChunkIndexes?: readonly number[],
		reason?: "expired" | "network"
	): Promise<string>;
}> {
	const activeChunkUrls = [...input.chunkUrls];
	const activeChunkHeaders = [...(input.chunkHeaders ?? [])];
	let activeBatchRefresh: Promise<void> | undefined;
	let refreshQueue = Promise.resolve();
	const enqueueRefresh = (work: () => Promise<void>): Promise<void> => {
		const queued = refreshQueue.then(work);
		refreshQueue = queued.then(
			() => undefined,
			() => undefined
		);
		return queued;
	};
	return {
		chunkUrls: activeChunkUrls,
		chunkHeaders: activeChunkHeaders,
		async refreshPresignedUrl(chunkIndex, expiredUrl, pendingChunkIndexes, reason) {
			assertValidChunkIndex(chunkIndex, input.session.totalChunks);
			// See the persisted path above: do not let a concurrent non-regional refresh satisfy a
			// regional recovery request merely because it changed the active URL.
			if (reason !== "network" && expiredUrl !== undefined && activeChunkUrls[chunkIndex] !== expiredUrl) {
				return activeChunkUrls[chunkIndex];
			}
			const refreshBatch = async () => {
				const refreshChunkIndexes = pendingChunkIndexes ?? [chunkIndex];
				const partNumbers = refreshChunkIndexes.map((pendingChunkIndex) => {
					assertValidChunkIndex(pendingChunkIndex, input.session.totalChunks);
					return pendingChunkIndex + 1;
				});
				const refreshOperation = partNumbers.length === 1 ? "refresh-direct-part" : "refresh-direct-parts";
				const refreshedUrls = await runUrlRefreshStage(refreshOperation, input.session, input.ports.telemetry, async () => {
					const results = normalizeRefreshBatch(
						input.session,
						reason === "network"
							? await input.ports.multipartApi.refreshUrls(input.session, partNumbers, true)
							: await input.ports.multipartApi.refreshUrls(input.session, partNumbers)
					);
					assertExactRefreshResults(
						results.updates,
						results.targetMigration
							? Array.from({ length: input.session.totalChunks }, (_value, index) => index + 1)
							: partNumbers,
						"Multipart URL refresh"
					);
					return results;
				});
				if (input.ownsCurrentSession && !(await input.ownsCurrentSession())) {
					throw new UploadOwnershipLostError();
				}
				const refresh = await runUrlRefreshStage(refreshOperation, input.session, input.ports.telemetry, async () => {
					assertDirectRefreshTargetFence(input.session, refreshedUrls.updates, refreshedUrls.targetMigration);
					return refreshedUrls;
				});
				const { updates, targetMigration } = refresh;
				if (input.session.ephemeral) {
					for (const update of updates) activeChunkUrls[update.partNumber - 1] = update.url;
					mergeRefreshedEnvelopeHeaders(activeChunkHeaders, updates);
					return;
				}
				const persistenceSession = targetMigration
					? ({
							...input.session,
							attemptId: targetMigration.replacementTarget.attemptId,
							generation: targetMigration.replacementTarget.generation
						} as Session)
					: input.session;
				const persistOperation = partNumbers.length === 1 ? "persist-direct-part-url" : "persist-direct-part-urls";
				const persistMergedUrls = () =>
					runUrlRefreshStage(persistOperation, input.session, input.ports.telemetry, () =>
						targetMigration
							? input.ports.sessionStore.mergeAndPersistUrls(
									persistenceSession,
									updates,
									activeChunkUrls,
									true,
									targetMigration
								)
							: input.ports.sessionStore.mergeAndPersistUrls(
									persistenceSession,
									updates,
									activeChunkUrls,
									pendingChunkIndexes !== undefined && reason !== "network"
								)
					);
				let mergedUrls: string[] | undefined;
				if (input.lease) {
					const mergeOutcome = await runGuardedEffect(input.lease, input.session, async () => {
						mergedUrls = await persistMergedUrls();
					});
					if (mergeOutcome === "skipped-not-owned") throw new UploadOwnershipLostError();
				} else {
					mergedUrls = await persistMergedUrls();
				}
				if (!mergedUrls) throw new UploadOwnershipLostError();
				if (input.ownsCurrentSession && !(await input.ownsCurrentSession())) throw new UploadOwnershipLostError();
				await runUrlRefreshStage(persistOperation, input.session, input.ports.telemetry, async () => {
					assertExactPresignedUrls(mergedUrls, input.session.totalChunks, "Direct multipart URL merge");
				});
				activeChunkUrls.splice(0, activeChunkUrls.length, ...mergedUrls);
				if (targetMigration) {
					activeChunkHeaders.splice(
						0,
						activeChunkHeaders.length,
						...targetMigration.replacementTarget.envelopes.map(({ headers }) => headers)
					);
					Object.assign(input.session, {
						attemptId: targetMigration.replacementTarget.attemptId,
						generation: targetMigration.replacementTarget.generation,
						takeGeneration: targetMigration.replacementTarget.takeGeneration,
						targetEnvelopesJson: JSON.stringify(targetMigration.replacementTarget.envelopes),
						chunkUrlsJson: JSON.stringify(mergedUrls)
					});
				} else {
					mergeRefreshedEnvelopeHeaders(activeChunkHeaders, updates);
				}
			};
			if (reason === "network") {
				await enqueueRefresh(refreshBatch);
				return activeChunkUrls[chunkIndex];
			}
			activeBatchRefresh ??= enqueueRefresh(refreshBatch).finally(() => {
				activeBatchRefresh = undefined;
			});
			await activeBatchRefresh;
			return activeChunkUrls[chunkIndex];
		}
	};
}
